import { showAlertMsg } from '../show-alert-msg';
import { createRequireDependency } from '../require-dependency';
import { reloadPledgesPage as reloadPledgesPageFromRegistry } from './runtime-registry';
import { withOverlayLoader, fetchPledgeModalValues, handleAjaxSuccess, handleAjaxError } from '../../helpers.js';
import { validatePledgeAmountInput, closeEditOngoingPledgeModal } from '../../pledge-pay/pledge-pay-recurring.js';
import { cleanupBootstrapModalBody } from '../../shared/bootstrap-modal-cleanup.js';
import { getManagerNavAction } from '../manager-nav-registry.js';
import { runMemberNavAction } from '../../members/member-nav-registry.js';
import { openIndexPage } from '../../members/member-open-index-page.js';
import * as FieldValidation from '../../shared/field-validation.js';
import { buildAddPaymentMethodPayload, formatCardNumberInput, readAddFormValues } from '../../shared/payment-method-form.js';
import { clearSavedCardSelection, renderSavedCards } from '../../shared/saved-card-picker.js';
import {
    addedSavedCardId,
    applySimpleRecurringPlan,
    clearEditRecurringFormError,
    currentSavedCardIds,
    isFinitePledgeRecurringEdit,
    loadEditRecurringSavedCards,
    remainingBalanceChangedFromBaseline,
    restoreEditRecurringBaseline,
    selectedScheduleEditTarget,
    setEditRecurringFormError,
    setManualCardCollapsed,
    updateEditRecurringModePanel,
} from '../../shared/ongoing-pledge-edit-ui.js';

const MODAL_SELECTOR = '#PledgeEditOngoingModal';
const CONFIRM_SELECTOR = '#CancelRecurringConfirmModal';
const INIT_FLAG = 'pledgeEditOngoingModalInitialized';
const BIND_NAMESPACE = '.pledgeEditOngoingModal';
const CONFIG_SCRIPT_ID = 'pledge-edit-ongoing-modal-config';
const PICKER_NAME = 'edit_recurring_cc_id';
const ADD_FIELDS = {
    name: '#er_name',
    card: '#er_card',
    expiryMonth: '#er_expiry_month',
    expiryYear: '#er_expiry_year',
    cvv2: '#er_cvv2',
    avsZip: '#er_avs_zip',
    useOtp: '#er_use_otp',
};

function filledAjaxUrl(template, id) {
    return String(template || '')
        .replace(':pledge_id', encodeURIComponent(id))
        .replace(':ongoing_record_id', encodeURIComponent(id))
        .replace(':id', encodeURIComponent(id));
}

export function openCancelRecurringConfirm(ongoingId, pledgeType) {
    const $confirm = $(CONFIRM_SELECTOR);
    if (!$confirm.length) {
        throw new Error('openCancelRecurringConfirm: #CancelRecurringConfirmModal is required');
    }
    $confirm.data('ongoingId', String(ongoingId || ''));
    $confirm.data('pledgeType', String(pledgeType || ''));
    $confirm.modal('show');
}

function selectedGatewayId($modal, config) {
    const fromCampaign = Number($modal.find('#on_campaign_id option:selected').attr('data-gateway-id') || 0);
    return fromCampaign || Number(config.gatewayId || 0);
}

function onEditRecurringAddNewCard(event) {
    const picker = event.detail && event.detail.picker;
    if (!picker || picker.getAttribute('data-saved-card-picker') !== PICKER_NAME) {
        return;
    }
    const $modal = $(MODAL_SELECTOR);
    clearSavedCardSelection(picker);
    setManualCardCollapsed($modal, false);
}

export function readPledgeEditOngoingModalConfig() {
    const el = document.getElementById(CONFIG_SCRIPT_ID);
    if (!el || !el.textContent) {
        throw new Error('readPledgeEditOngoingModalConfig: #' + CONFIG_SCRIPT_ID + ' is required');
    }
    let config;
    try {
        config = JSON.parse(String(el.textContent).trim());
    } catch (err) {
        throw new Error('readPledgeEditOngoingModalConfig: #' + CONFIG_SCRIPT_ID + ' contains invalid JSON');
    }
    if (!config || typeof config !== 'object' || Array.isArray(config)) {
        throw new Error('readPledgeEditOngoingModalConfig: #' + CONFIG_SCRIPT_ID + ' must contain a JSON object');
    }
    if (!String(config.saveUrl || '').trim()) {
        throw new Error('readPledgeEditOngoingModalConfig: saveUrl is required');
    }
    if (!String(config.csrfToken || '').trim()) {
        throw new Error('readPledgeEditOngoingModalConfig: csrfToken is required');
    }
    return config;
}

export function setInstallmentAmountEditable($modal, isEditable) {
    $modal.find('#on_amount').prop('disabled', !isEditable);
}

export function setInstallmentAmountTooltipVisible($modal, isVisible) {
    $modal.find('#installment_amount_note').toggleClass('d-none', !isVisible);
}

function validateRecurringInstallmentMinimum($modal, minTransAmt) {
    const installmentAmount = parseFloat(String($modal.find('#on_amount').val() || '').replace(/,/g, '')) || 0;
    const minAmount = Number(minTransAmt) || 0;
    const valid = installmentAmount >= minAmount;
    $modal.find('#enter_amt_err').prop('hidden', valid);
    return valid;
}

export function initPledgeEditOngoingModal(config, deps) {
    const $modal = $(MODAL_SELECTOR);
    if (!$modal.length) {
        return null;
    }
    if ($modal.data(INIT_FLAG)) {
        return $modal.data('pledgeEditOngoingModalApi');
    }

    const isMemberPortal = config.portal === 'member';
    const requireDep = createRequireDependency(deps, 'initPledgeEditOngoingModal');
    const showAlert = requireDep('showAlertMsg');
    const ajaxWithOverlay = requireDep('withOverlayLoader');
    let reloadPledgesPage = function () {};
    let openMgrIndexPage = function () {};
    let reloadMemberPledgesPage = function () {};
    let openMemberIndexPage = function () {};
    if (isMemberPortal) {
        reloadMemberPledgesPage = requireDep('reloadMemberPledgesPage');
        openMemberIndexPage = requireDep('openMemberIndexPage');
    } else {
        reloadPledgesPage = requireDep('reloadPledgesPage');
        openMgrIndexPage = requireDep('openMgrIndexPage');
    }

    function reloadAfterSave() {
        if (isMemberPortal) {
            cleanupBootstrapModalBody();
            reloadMemberPledgesPage();
            return;
        }
        const ongoingType = String($modal.find('#ongoing_type').val() || '').trim();
        reloadPledgesPage(undefined, [{
            table_name: ongoingType === 'Donation' ? 'ongoing_donations' : 'pledge',
            id: $modal.find('#ongoing_id').val(),
        }]);
    }

    function ajaxErrorHandler(xhr) {
        handleAjaxError({
            xhr: xhr,
            redirect: isMemberPortal ? openMemberIndexPage : openMgrIndexPage,
        });
    }

    function ajaxSaveSuccess(ongoingType) {
        return function (response) {
            handleAjaxSuccess(response, {
                alertMessage: 'The ongoing ' + ongoingType + ' was successfully updated',
                redirect: reloadAfterSave,
            });
        };
    }

    $modal.find('#enter_amt_err').prop('hidden', true);
    $modal.data('editOngoingConfig', config);
    $modal.data('editOngoingMinTransAmt', config.minTransAmt);

    $(document)
        .off('click' + BIND_NAMESPACE, '[data-action="close-edit-ongoing-modal"]')
        .on('click' + BIND_NAMESPACE, '[data-action="close-edit-ongoing-modal"]', function () {
            closeEditOngoingPledgeModal();
        });

    $(document)
        .off('click' + BIND_NAMESPACE, '[data-action="confirm-cancel-recurring"]')
        .on('click' + BIND_NAMESPACE, '[data-action="confirm-cancel-recurring"]', function () {
            const $confirm = $(CONFIRM_SELECTOR);
            const ongoingId = String($confirm.data('ongoingId') || '');
            const pledgeType = String($confirm.data('pledgeType') || '');
            if (!ongoingId || !pledgeType) {
                showAlert('Unable to cancel this schedule.', 'danger');
                return;
            }
            $confirm.modal('hide');
            cleanupBootstrapModalBody();

            function postCancel(requestData) {
                if (isMemberPortal) {
                    $.ajax({
                        url: config.saveUrl,
                        type: 'POST',
                        data: requestData,
                        success: ajaxSaveSuccess(pledgeType),
                        error: ajaxErrorHandler,
                    });
                    return;
                }
                ajaxWithOverlay({
                    url: config.saveUrl,
                    type: 'POST',
                    data: requestData,
                    success: ajaxSaveSuccess(pledgeType),
                    error: ajaxErrorHandler,
                });
            }

            function payloadFromRecord(record) {
                const installment = record.installment_amount ?? record.amount ?? 0;
                const numLeft = record.ongoing_indefinitely ? 9999 : (parseInt(record.num_left, 10) || 0);
                const requestData = {
                    user_id: record.user_id,
                    num_left: numLeft,
                    frequency_id: record.frequency_id,
                    ongoing_donation_id: ongoingId,
                    ongoing_type: pledgeType,
                    cancel_recur: 'true',
                    campaign_id: record.campaign_id,
                    custom_description: record.comments ?? record.description ?? '',
                    amount: installment,
                    installment_amount: installment,
                    _token: config.csrfToken,
                };
                if (!isMemberPortal) {
                    requestData.name = record.name ?? '';
                    requestData.email = record.email ?? '';
                    requestData.phone = record.phone ?? '';
                }
                return requestData;
            }

            if (pledgeType === 'Donation') {
                fetch(filledAjaxUrl(config.ongoingAjaxUrl, ongoingId), {
                    headers: { 'X-CSRF-TOKEN': config.csrfToken, 'X-Requested-With': 'XMLHttpRequest' },
                })
                    .then((response) => response.json())
                    .then((payload) => {
                        if (!payload || !payload.success) {
                            showAlert('The recurring donation could not be loaded.', 'danger');
                            return;
                        }
                        postCancel(payloadFromRecord(payload));
                    })
                    .catch(ajaxErrorHandler);
                return;
            }

            fetchPledgeModalValues(ongoingId, config.pledgeAjaxUrl).then((payload) => {
                if (!payload || !payload.success || !payload.data) {
                    showAlert('The recurring pledge could not be loaded.', 'danger');
                    return;
                }
                postCancel(payloadFromRecord(payload.data));
            });
        });

    $modal
        .off('change' + BIND_NAMESPACE, 'input[name="edit_recurring_change"]')
        .on('change' + BIND_NAMESPACE, 'input[name="edit_recurring_change"]', function () {
            restoreEditRecurringBaseline($modal);
            clearEditRecurringFormError($modal);
            updateEditRecurringModePanel($modal, config.minTransAmt);
        });

    $modal
        .off('change' + BIND_NAMESPACE, '#edit_recurring_cc_id')
        .on('change' + BIND_NAMESPACE, '#edit_recurring_cc_id', function () {
            if (String($(this).val() || '').trim()) {
                setManualCardCollapsed($modal, true);
            }
        });

    $modal
        .off('input' + BIND_NAMESPACE, '#er_card')
        .on('input' + BIND_NAMESPACE, '#er_card', function () {
            formatCardNumberInput(this);
        });

    $modal
        .off('click' + BIND_NAMESPACE, '[data-action="edit-recurring-save-card"]')
        .on('click' + BIND_NAMESPACE, '[data-action="edit-recurring-save-card"]', function () {
            const $name = $modal.find('#er_name');
            if (!String($name.val() || '').trim()) {
                FieldValidation.reportFieldError($name, FieldValidation.messages.card.name);
                return;
            }
            const addRoot = $modal.find('[data-edit-recurring-manual-card]')[0];
            if (!addRoot || !FieldValidation.validateCardPaymentFields(addRoot, null, { requireCreditFields: true })) {
                return;
            }
            const gateId = selectedGatewayId($modal, config);
            if (!gateId) {
                setEditRecurringFormError($modal, 'No payment gateway is configured for this campaign.');
                return;
            }
            const extras = {
                gate_id: gateId,
                _token: config.csrfToken,
            };
            if (isMemberPortal) {
                extras.user_type = config.userType;
            } else {
                extras.member_id = $modal.find('#ongoing_user_id').val();
            }
            let payload;
            try {
                payload = buildAddPaymentMethodPayload(
                    Object.assign(readAddFormValues(ADD_FIELDS), { cardType: 'credit', useOtp: false }),
                    extras
                );
            } catch (err) {
                setEditRecurringFormError($modal, String(err));
                return;
            }

            const previousIds = currentSavedCardIds($modal);
            const userId = $modal.find('#ongoing_user_id').val();
            const pledgeType = $modal.find('#ongoing_type').val();
            const pledgeData = $modal.data('editRecurringPledgeData') || {};

            function afterCardSaved(response) {
                if (!response || !response.success) {
                    setEditRecurringFormError($modal, (response && response.error) || 'The payment method could not be saved.');
                    return;
                }
                loadEditRecurringSavedCards(
                    $modal,
                    config.savedPaymentsUrl,
                    userId,
                    pledgeData,
                    pledgeType
                ).then((cards) => {
                    const added = addedSavedCardId(cards, previousIds);
                    renderSavedCards(PICKER_NAME, cards, added);
                    setManualCardCollapsed($modal, true);
                });
            }

            if (isMemberPortal) {
                $.ajax({
                    url: config.addCardSaveUrl,
                    type: 'POST',
                    data: payload,
                    success: afterCardSaved,
                    error: function () {
                        setEditRecurringFormError($modal, 'The request timed out. Try saving the card again.');
                    },
                });
                return;
            }

            ajaxWithOverlay({
                url: config.addCardSaveUrl,
                type: 'POST',
                data: payload,
                success: afterCardSaved,
                error: function () {
                    setEditRecurringFormError($modal, 'The payment method could not be saved. Try again.');
                },
            });
        });

    document.removeEventListener('saved-card:add-new', onEditRecurringAddNewCard);
    document.addEventListener('saved-card:add-new', onEditRecurringAddNewCard);

    if (!config.kioskValidated) {
        FieldValidation.applyCardMasks($modal);
    }
    FieldValidation.applyCardConstraints($modal);

    $modal.find('.edit-recurring-modal__form')
        .off('submit' + BIND_NAMESPACE)
        .on('submit' + BIND_NAMESPACE, function (e) {
            e.preventDefault();
            const $form = $(this);
            const ongoingType = $modal.find('#ongoing_type').val();
            const $onAmount = $form.find('#on_amount');
            clearEditRecurringFormError($modal);

            applySimpleRecurringPlan($modal, config.minTransAmt);
            if (!validateRecurringInstallmentMinimum($modal, config.minTransAmt)) {
                setEditRecurringFormError($modal, 'Each payment is below the minimum amount.');
                return false;
            }
            if (ongoingType === 'Pledge' && !validatePledgeAmountInput($modal)) {
                setEditRecurringFormError($modal, 'Remaining cannot be below $0.');
                return false;
            }
            const selectedNumLeft = parseInt($form.find('#on_num_left').val(), 10);
            if (!Number.isInteger(selectedNumLeft)) {
                setEditRecurringFormError($modal, 'Enter how many payments are left.');
                return false;
            }
            if (isFinitePledgeRecurringEdit($modal)) {
                const baselineNumLeft = parseInt($modal.data('editRecurringBaselineNumLeft'), 10) || 0;
                const scheduleChanged = remainingBalanceChangedFromBaseline($modal)
                    || selectedNumLeft !== baselineNumLeft;
                if (scheduleChanged && !selectedScheduleEditTarget($modal)) {
                    setEditRecurringFormError($modal, 'Choose Balance or Payments.');
                    return false;
                }
            }
            const frequencyMax = (config.frequencyMaxInstallments || {})[$form.find('#on_frequency_id').val()];
            if (Number.isFinite(frequencyMax) && selectedNumLeft > frequencyMax) {
                setEditRecurringFormError($modal, 'This frequency allows at most ' + frequencyMax + ' payments left.');
                return false;
            }

            const campaign_id = $form.find('#on_campaign_id').val();
            const $pledgeAmountInput = $modal.find('#pledge_amount_input');
            const requestNumLeft = selectedNumLeft === 0 ? 9999 : selectedNumLeft;

            const requestData = {
                user_id: $modal.find('#ongoing_user_id').val(),
                num_left: requestNumLeft,
                frequency_id: $form.find('#on_frequency_id').val(),
                ongoing_donation_id: $modal.find('#ongoing_id').val(),
                ongoing_type: ongoingType,
                cancel_recur: 'false',
                campaign_id: campaign_id,
                custom_description: $modal.find('#on_custom_desc').val(),
                _token: config.csrfToken,
            };

            const selectedCcId = String($modal.find('#edit_recurring_cc_id').val() || '').trim();
            if (selectedCcId) {
                requestData.cc_id = selectedCcId;
            }

            if (!isMemberPortal) {
                requestData.name = $modal.find('#ongoing_user_name').val();
                requestData.email = $modal.find('#ongoing_user_email').val();
                requestData.phone = $modal.find('#ongoing_user_phone').val();
            }

            requestData.installment_amount = $onAmount.val();
            requestData.amount = $onAmount.val();
            if (ongoingType === 'Pledge' && !$pledgeAmountInput.prop('disabled') && !$modal.find('#pledge_totals_row').hasClass('d-none')) {
                requestData.pledge_amount = $pledgeAmountInput.val();
            }

            function afterEditSaved(response) {
                if (!response || !response.success) {
                    setEditRecurringFormError($modal, (response && response.error) || 'The recurring schedule could not be updated.');
                    return;
                }
                $modal.modal('hide');
                cleanupBootstrapModalBody();
                localStorage.clear();
                ajaxSaveSuccess(ongoingType)(response);
            }

            if (isMemberPortal) {
                $.ajax({
                    url: config.saveUrl,
                    type: 'POST',
                    data: requestData,
                    success: afterEditSaved,
                    error: ajaxErrorHandler,
                });
                return false;
            }

            ajaxWithOverlay({
                url: config.saveUrl,
                type: 'POST',
                data: requestData,
                success: afterEditSaved,
                error: ajaxErrorHandler,
            });
        });

    if (config.useNumericAmountInput) {
        $modal.find('#on_amount').prop({ type: 'number' });
    }

    const api = {
        setInstallmentAmountEditable,
        setInstallmentAmountTooltipVisible,
        validateRecurringInstallmentMinimum: function ($modalEl) {
            return validateRecurringInstallmentMinimum($modalEl, config.minTransAmt);
        },
    };
    $modal.data(INIT_FLAG, true);
    $modal.data('pledgeEditOngoingModalApi', api);
    return api;
}

export function createDefaultPledgeEditOngoingModalDeps() {
    function noop() {}
    function resolveOpenMgrIndexPage() {
        try {
            return getManagerNavAction('openMgrIndexPage');
        } catch (err) {
            return noop;
        }
    }
    function resolveOpenMemberIndexPage() {
        try {
            return getManagerNavAction('openMemberIndexPage');
        } catch (err) {
            if (typeof openIndexPage === 'function') {
                return openIndexPage;
            }
            return noop;
        }
    }
    return {
        showAlertMsg,
        withOverlayLoader: withOverlayLoader,
        reloadPledgesPage: reloadPledgesPageFromRegistry,
        openMgrIndexPage: resolveOpenMgrIndexPage(),
        reloadMemberPledgesPage: function () {
            runMemberNavAction('OpenMyPledges');
        },
        openMemberIndexPage: resolveOpenMemberIndexPage(),
    };
}